/**
 * KOKOCHAO V15.0.2 — Entity Discovery, Horizon-powered.
 * Uses the Horizon SectionRenderer for cache, request deduplication and cancellation semantics.
 * Supports collection-filter discovery first, then storefront-search fallback.
 */
import { sectionRenderer } from '@theme/section-renderer';

const SELECTOR = '[data-kc-entity-products-fallback]';

const parseGrid = (html) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const grid = doc.querySelector('[data-kc-entity-product-grid]');
  if (!grid || !grid.querySelector('.grid__item')) return null;
  return grid;
};

const renderFrom = async (url, sectionId) => {
  if (!url || !sectionId) return null;
  try {
    const html = await sectionRenderer.getSectionHTML(sectionId, true, url);
    return parseGrid(html);
  } catch (_) {
    return null;
  }
};

const buildSearchUrl = (host) => {
  const query = (host.dataset.searchQuery || '').trim();
  if (!query) return null;
  const url = new URL(host.dataset.searchRoute || '/search', window.location.origin);
  url.searchParams.set('q', query);
  url.searchParams.set('type', 'product');
  url.searchParams.set('options[prefix]', 'last');
  return url;
};

const hydrate = async (host) => {
  if (!(host instanceof HTMLElement) || host.dataset.kcEntityHydrated === 'true') return;
  host.dataset.kcEntityHydrated = 'true';

  const sectionId = host.dataset.discoverySection || 'kc-entity-discovery-products';
  const sourceUrlRaw = (host.dataset.sourceUrl || '').trim();
  const sourceUrl = sourceUrlRaw ? new URL(sourceUrlRaw, window.location.origin) : null;

  // Structured collection/filter source takes priority when available.
  let grid = sourceUrl ? await renderFrom(sourceUrl, sectionId) : null;

  // Search remains the resilient fallback for incomplete metaobject mappings.
  if (!grid) {
    const searchUrl = buildSearchUrl(host);
    grid = searchUrl ? await renderFrom(searchUrl, sectionId) : null;
  }

  if (!grid) return;

  host.replaceChildren(grid);
  host.classList.add('is-hydrated');
  host.dispatchEvent(new CustomEvent('kc:entity-products-loaded', {
    bubbles: true,
    detail: {
      entityType: host.dataset.entityType || '',
      entityHandle: host.dataset.entityHandle || '',
    },
  }));
};

const init = (root = document) => {
  root.querySelectorAll?.(SELECTOR).forEach((host) => void hydrate(host));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
} else {
  init(document);
}

document.addEventListener('shopify:section:load', (event) => init(event.target));
